import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import "./RequestDetailsSheet.css";

export default function RequestDetailsSheet({
  request,
  onClose,
  formatDate,
  formatTime,
  getDuration,
  busy = false,
  children,
}) {
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  const busyRef = useRef(busy);
  closeRef.current = onClose;
  busyRef.current = busy;
  useEffect(() => {
    const previousFocus = document.activeElement;
    panelRef.current?.querySelector("button")?.focus();
    const handleKey = (event) => {
      if (event.key === "Escape" && !busyRef.current) closeRef.current();
      if (event.key === "Tab") {
        const controls = [
          ...panelRef.current.querySelectorAll(
            'button:not(:disabled), textarea:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]',
          ),
        ];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        }
        if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      previousFocus?.focus();
    };
  }, []);

  const fields = [
    ["Status", request.status || "Pending"],
    ["Employee Code", request.employee_code || "—"],
    [
      "Reporting Manager",
      request.manager_name || request.reporting_manager_name || "—",
    ],
    ["In Date", formatDate(request.in_datetime)],
    ["From Time", formatTime(request.in_datetime)],
    ["Out Date", formatDate(request.out_datetime)],
    ["To Time", formatTime(request.out_datetime)],
    ["Duration", getDuration(request.in_datetime, request.out_datetime)],
    ["Requested", formatDate(request.created_at)],
  ];

  return createPortal(
    <div className="request-details-overlay" onClick={() => !busy && onClose()}>
      <section
        ref={panelRef}
        className="request-details-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-details-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="request-details-header">
          <div>
            <h2 id="request-details-title">Request Details</h2>
            <p>Request #{request.id}</p>
          </div>
          <button
            type="button"
            className="request-details-close"
            aria-label="Close request details"
            onClick={onClose}
            disabled={busy}
          >
            <FiX />
          </button>
        </header>
        <div className="request-details-body">
          <dl className="request-details-grid">
            {fields.map(([label, value]) => (
              <div
                key={label}
                className={`request-details-field${label === "Status" ? " request-details-status" : ""}`}
              >
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
            {request.remark && (
              <div className="request-details-field request-details-wide">
                <dt>Employee Remark</dt>
                <dd>{request.remark}</dd>
              </div>
            )}
          </dl>
          {children}
        </div>
      </section>
    </div>,
    document.body,
  );
}
